import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, Crown, ArrowRight, Loader2, UserMinus } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const PUBLIC_PATHS = ['/', '/login', '/register', '/pricing', '/forgot-password', '/terms', '/privacy', '/cookies', '/confidentiality', '/about', '/contact', '/careers', '/partners', '/blog', '/help', '/support', '/tutorials', '/shared', '/esign/sign', '/directory'];

const ExpiredPlanModal = () => {
  const { user, isAuthenticated, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [reverting, setReverting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isExpired = isAuthenticated && user?.subscription?.status === 'expired';
  const isPublicPath = PUBLIC_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  const shouldShow = isExpired && !isPublicPath && !dismissed;

  const planName = user?.subscription?.planName || user?.subscription?.plan || 'your plan';
  const expiredAt = user?.subscription?.expiresAt ? new Date(user.subscription.expiresAt).toLocaleDateString() : null;

  const handleRevertToFree = async () => {
    setReverting(true);
    try {
      await api.post('/api/subscriptions/revert-to-free');
      updateUser({
        subscription: { plan: 'free', planName: 'Free', status: 'active', billingPeriod: 0, monthlyPrice: 0, expiresAt: null }
      });
      toast.success('You are now on the Free plan. You can upgrade anytime.');
      setDismissed(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch to free plan');
    } finally {
      setReverting(false);
    }
  };

  const handleChoosePlan = () => {
    setDismissed(true);
    navigate('/pricing');
  };

  if (!shouldShow) return null;

  return (
    <div className="fixed inset-0 z-[60]" style={{ pointerEvents: 'none' }}>
      <Dialog open={true}>
        <DialogContent
          className="sm:max-w-md dark:bg-gray-900 dark:border-gray-700 border-red-200"
          style={{ zIndex: 60, pointerEvents: 'auto' }}
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <DialogTitle className="dark:text-white text-lg">
                  Your Plan Has Expired
                </DialogTitle>
              </div>
            </div>
            <DialogDescription className="dark:text-gray-400 pt-2">
              Your <span className="font-semibold text-gray-700 dark:text-gray-200">{planName}</span> plan
              {expiredAt && <> expired on <span className="font-medium">{expiredAt}</span></>}.
              Choose how you'd like to continue:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-4">
            <Button
              className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12 text-sm font-semibold"
              onClick={handleChoosePlan}
              data-testid="expired-choose-plan-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              Choose a New Plan
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <Button
              variant="outline"
              className="w-full h-12 text-sm dark:border-gray-700 dark:text-gray-300"
              onClick={handleRevertToFree}
              disabled={reverting}
              data-testid="expired-free-member-btn"
            >
              {reverting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserMinus className="w-4 h-4 mr-2" />
              )}
              Continue as Free Member
            </Button>

            <p className="text-xs text-center text-gray-400 dark:text-gray-500 pt-1">
              Free members have limited access to deals, comps, and reports.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpiredPlanModal;
